import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { BACKDROP, BORDER, RADIUS_SHEET, SAFE_BOTTOM, TEXT } from "./tokens";

export interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  /** Optional 18px/600 heading under the grabber. */
  title?: string;
  children: React.ReactNode;
  /**
   * Pinned below the scrolling body (a Cancel / primary row, a Show button).
   * Stays visible while the body scrolls.
   */
  footer?: React.ReactNode;
  /** Pinned above the scrolling body (a search box). */
  header?: React.ReactNode;
}

/**
 * Rounded bottom sheet with a grabber. Portals to document.body so it escapes
 * the page's own scroll container; backdrop tap and Escape close it; the body
 * scroll is locked while open; the panel caps at 88vh and scrolls internally
 * with safe-area padding at the bottom.
 */
const BottomSheet: React.FC<BottomSheetProps> = ({ open, onClose, title, children, footer, header }) => {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseRef.current();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <>
      <div
        data-sheet-backdrop=""
        onClick={onClose}
        style={{ position: "fixed", inset: 0, backgroundColor: BACKDROP, zIndex: 190 }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          flexDirection: "column",
          maxHeight: "88vh",
          backgroundColor: "#FFFFFF",
          borderRadius: `${RADIUS_SHEET} ${RADIUS_SHEET} 0 0`,
          boxShadow: "0 -12px 40px rgba(0,0,0,.12)",
          zIndex: 200,
        }}
      >
        <div className="flex justify-center" style={{ padding: "10px 0 6px", flex: "none" }}>
          <div style={{ width: "40px", height: "4px", borderRadius: "999px", backgroundColor: BORDER }} />
        </div>
        {title && (
          <div
            className="font-inter"
            style={{ padding: "4px 20px 12px", fontSize: "18px", fontWeight: 600, color: TEXT, flex: "none" }}
          >
            {title}
          </div>
        )}
        {header && <div style={{ flex: "none" }}>{header}</div>}
        <div
          style={{
            flex: "1 1 auto",
            minHeight: 0,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            paddingBottom: footer ? "8px" : `calc(16px + ${SAFE_BOTTOM})`,
          }}
        >
          {children}
        </div>
        {footer && (
          <div style={{ flex: "none", paddingBottom: SAFE_BOTTOM }}>{footer}</div>
        )}
      </div>
    </>,
    document.body,
  );
};

export default BottomSheet;
